import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ReactNode} from 'react';

import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import VisualizationConfigEditor from 'insights/analytics-visualizations/configs/VisualizationConfigEditor';
import {
  VisualizationConfig,
  VisualizationType,
} from 'insights/universal-blocks/SharedUniversalBlockViewTypes';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';

import {
  SHARED_CONFIRMATION_BUTTON_STYLES,
  SHARED_RIGHT_PANE_STYLES,
  SHARED_RIGHT_PANE_FOOTER_STYLES,
} from '../VisualizationStepEditorConstants';

export const DONE_BUTTON_TEXT = 'Return to analysis';

export interface EditorSidebarProps {
  visualizationConfig: VisualizationConfig | null;
  onChangeVisualizationConfig: (newVisualizationConfig: VisualizationConfig) => void;
  visualizationEditorIsDisabled?: boolean;
  dataFrameColumnDefs: Array<DataFrameColumnDef>;
  typeSelectorIsDisabled?: boolean;
  doneButtonIsDisabled: boolean;
  doneButtonText?: string;
  onClickDoneButton: () => Promise<void>;
  disabledVisualizationTypes?: Array<VisualizationType>;
  children: ReactNode;
  enableAnalysisTab?: boolean;
  enableAggregation?: boolean;
  enableFormatTab?: boolean;
  enableFormatTabForLineChart?: boolean;
  enableErrorBarColumn?: boolean;
}

export default function EditorSidebar({
  visualizationConfig,
  onChangeVisualizationConfig,
  visualizationEditorIsDisabled = false,
  dataFrameColumnDefs,
  typeSelectorIsDisabled = false,
  doneButtonText,
  doneButtonIsDisabled,
  onClickDoneButton,
  disabledVisualizationTypes,
  children,
  enableAnalysisTab,
  enableAggregation,
  enableFormatTab,
  enableFormatTabForLineChart,
  enableErrorBarColumn,
}: EditorSidebarProps): JSX.Element {
  return (
    <div className={classNames(css(visualizationSidebarStyles.pane), 'scrollbar-override')}>
      <VisualizationConfigEditor
        columns={dataFrameColumnDefs}
        typeSelectorIsDisabled={typeSelectorIsDisabled}
        disabledVisualizationTypes={disabledVisualizationTypes ?? []}
        isAnalysisTool={true}
        onChangeVisualizationConfig={onChangeVisualizationConfig}
        visualizationConfig={visualizationConfig}
        useSystemNames={true}
        enableAnalysisTab={enableAnalysisTab}
        enableAggregation={enableAggregation}
        enableFormatTab={enableFormatTab}
        enableFormatTabForLineChart={enableFormatTabForLineChart}
        enableErrorBarColumn={enableErrorBarColumn}
        isDisabled={visualizationEditorIsDisabled}
      />
      {children}
      <div className="u-input-spacing-top" />
      <div className={css(visualizationSidebarStyles.paneFooter)}>
        <Button
          buttonStyle={ButtonStyle.SECONDARY}
          fullWidth={true}
          className={css(visualizationSidebarStyles.confirmationButton)}
          size={ButtonSize.MEDIUM}
          disabled={doneButtonIsDisabled}
          onClick={onClickDoneButton}
          text={doneButtonText ?? DONE_BUTTON_TEXT}
          dataTestAttrs={{
            component: 'VisualizationEditorSidebar',
            element: 'returnToAnalysisButton',
          }}
        />
      </div>
    </div>
  );
}

export const visualizationSidebarStyles = StyleSheet.create({
  confirmationButton: SHARED_CONFIRMATION_BUTTON_STYLES,
  pane: SHARED_RIGHT_PANE_STYLES,
  paneFooter: {
    ...SHARED_RIGHT_PANE_FOOTER_STYLES,
    // When the visualization config editor is tabbed, the selected tab's z-index is 1, so we have to enforce
    // that the sticky footer is displayed in front of the selected tab by setting z-index to 2
    zIndex: 2,
  },
});
